import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Drawer from '@mui/material/Drawer';
import { useEffect, useRef, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import RulesIcon from '@mui/icons-material/AutoAwesome';
import ModeStandbyIcon from '@mui/icons-material/ModeStandby';
import { useLocation } from 'react-router';
import { useBreakpoints } from 'hooks';
import { useCurrentNetwork } from 'hooks';
import LogoAutoRules from 'assets/logo-autorules.svg?react';
import SidebarNavItem from './SidebarNavItem';
import SettingsNav from './SettingsNav';
import UserNav from './UserNav';
import { useModal } from 'hooks';
import TopBar from './TopBar';
import type { Network } from 'types/shared';
import HelpNav from './HelpNav';

const Sidebar: FC = () => {
  const network = useCurrentNetwork();
  const location = useLocation();
  const { isOpen, open, close, toggle } = useModal();
  const { isBreakpoint } = useBreakpoints('sm');

  useEffect(() => {
    isBreakpoint ? close() : open();
  }, [isBreakpoint, close, open]);

  useEffect(() => {
    close();
  }, [location, close]);

  const RulesMenuItem = useMemo(
    () => (
      <SidebarNavItem
        to={`/networks/${network}/rules`}
        title="Rules"
        icon={<RulesIcon style={{ fontSize: 24, padding: 0 }} />}
        style={{ marginBottom: 12 }}
      />
    ),
    [network]
  );

  const MetricsMenuItem = useMemo(
    () => (
      <SidebarNavItem
        to={`/networks/${network}/metrics`}
        title="Metrics"
        icon={<ModeStandbyIcon style={{ fontSize: 24, padding: 0 }} />}
        style={{ marginBottom: 12 }}
      />
    ),
    [network]
  );

  const MenuItems = useRef<Record<Network, ReactNode>>({
    facebook: (
      <>
        {RulesMenuItem}
        {MetricsMenuItem}
      </>
    ),
    google: <>{RulesMenuItem}</>,
    snapchat: <>{RulesMenuItem}</>,
    tiktok: (
      <>
        {RulesMenuItem}
        {MetricsMenuItem}
      </>
    ),
    x: (
      <>
        {RulesMenuItem}
        {MetricsMenuItem}
      </>
    ),
  });

  return (
    <>
      {isBreakpoint && <TopBar onToggleMenu={toggle} />}
      <Drawer
        anchor={'left'}
        open={isOpen}
        onClose={close}
        variant={isBreakpoint ? 'temporary' : 'permanent'}
        elevation={0}
        sx={(t) => ({
          position: 'relative',
          zIndex: 7,
          flexShrink: 0,
          width: 64,
          [`& .MuiDrawer-paper`]: {
            position: isBreakpoint ? 'fixed' : 'relative',
            borderRightColor: isBreakpoint ? 'transparent' : t.palette.grey[50],
            overflow: 'hidden',
            borderRadius: 1,
            [t.breakpoints.down('sm')]: {
              left: 8,
              top: 8,
              bottom: 8,
              height: 'auto',
            },
          },
          [`& .MuiModal-backdrop`]: {
            backgroundColor: 'rgba(43, 43, 43, 0.20)',
          },
        })}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box
          component="nav"
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: 'column',
            flexGrow: 1,
            maxWidth: 64,
            width: 64,
            pt: isBreakpoint ? 0 : 4,
            pb: isBreakpoint ? 2 : 3,
            height: '100vh',
            backgroundColor: 'white',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <List sx={{ '&': { p: 0, display: 'flex', flexDirection: 'column', flexGrow: 1, width: '100%' } }}>
              {MenuItems.current[network]}
            </List>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <LogoAutoRules width={80} height={110} />
            <HelpNav />
            <SettingsNav />
            <UserNav />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Sidebar;
